/**
 * Client-side search. The whole unit list is in memory, so search never touches the network.
 *
 * Normalization makes "בגצ" match "בג״ץ": niqqud, quote marks and gershayim are dropped,
 * final letters fold to their regular form (ץ -> צ), and punctuation and maqaf become spaces.
 */
const FINALS: Record<string, string> = {
  ך: 'כ', // ך -> כ
  ם: 'מ', // ם -> מ
  ן: 'נ', // ן -> נ
  ף: 'פ', // ף -> פ
  ץ: 'צ', // ץ -> צ
};

export function norm(s: unknown): string {
  return (s == null ? '' : String(s))
    .toLowerCase()
    .replace(/־/g, ' ') // maqaf joins words
    .replace(/[֑-ׇ]/g, '') // niqqud and cantillation
    .replace(/["'`״׳“”‘’]/g, '') // quotes, gershayim, geresh
    .replace(/[ךםןףץ]/g, (c) => FINALS[c]!) // final -> regular letters
    .replace(/[-–—(),.:;/]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export interface Searchable {
  name: string;
  nameAr?: string;
  nameEn?: string;
  booth?: string;
  description?: string;
  keywords?: string[];
  domain?: string;
  region?: string;
}

/** The normalized text a unit is matched against. */
export function haystack(u: Searchable): string {
  return norm(
    [
      u.name,
      u.nameAr,
      u.nameEn,
      'דוכן ' + (u.booth || ''),
      u.description,
      (u.keywords || []).join(' '),
      u.domain,
      u.region,
    ].join(' '),
  );
}

export const searchTerms = (query: string): string[] => norm(query).split(' ').filter(Boolean);

/** Every term must appear somewhere in the unit's text. */
export function matchUnit(u: Searchable, terms: string[]): boolean {
  if (!terms.length) return true;
  const hay = haystack(u);
  return terms.every((t) => hay.includes(t));
}

export interface SearchIndex<T extends Searchable> {
  items: { unit: T; hay: string }[];
}

/** Precompute normalized text once, so each keystroke only does substring checks. */
export function buildIndex<T extends Searchable>(units: T[]): SearchIndex<T> {
  return { items: units.map((unit) => ({ unit, hay: haystack(unit) })) };
}

export interface UnitFilter {
  query?: string;
  domain?: string;
  region?: string;
  favoritesOnly?: boolean;
  favorites?: ReadonlySet<string>;
}

export function filterUnits<T extends Searchable & { id: string }>(
  index: SearchIndex<T>,
  f: UnitFilter,
): T[] {
  const terms = searchTerms(f.query ?? '');
  const out: T[] = [];
  for (const { unit, hay } of index.items) {
    if (f.domain && unit.domain !== f.domain) continue;
    if (f.region && unit.region !== f.region) continue;
    if (f.favoritesOnly && !f.favorites?.has(unit.id)) continue;
    if (terms.length && !terms.every((t) => hay.includes(t))) continue;
    out.push(unit);
  }
  return out;
}
